import "@/App.css";
import "./lib/apiClient"; // installs global axios interceptors (must be first)
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { LanguageProvider } from "./i18n/LanguageContext";
import { AuthProvider } from "./auth/AuthContext";
import AuthCallback from "./auth/AuthCallback";
import ProtectedRoute from "./auth/ProtectedRoute";

import HomePage from "./pages/HomePage";

// Code-split secondary routes — keeps initial bundle lean for PageSpeed.
// Homepage stays eager (it's the LCP target).
const BlogIndexPage = lazy(() => import("./pages/BlogIndexPage"));
const BlogPostPage = lazy(() => import("./pages/BlogPostPage"));
const CheckoutReturn = lazy(() => import("./pages/CheckoutReturn"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const PortalRequestPage = lazy(() => import("./pages/PortalRequestPage"));
const PortalPage = lazy(() => import("./pages/PortalPage"));
const SupportPage = lazy(() => import("./pages/SupportPage"));
const CasePage = lazy(() => import("./pages/CasePage"));
const AdrustPage = lazy(() => import("./pages/AdrustPage"));

function RouteFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center text-sm text-ink">
      <span>Laden…</span>
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  // CRITICAL: handle OAuth callback before any route logic runs
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        {/* NL routes (source of truth for structure) */}
        <Route path="/" element={<HomePage />} />
        <Route path="/blog" element={<BlogIndexPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="/werk/:slug" element={<CasePage />} />
        <Route path="/adrust" element={<AdrustPage />} />

        {/* EN mirror routes. Same components — LanguageContext derives lang
             from the URL path so no prop-drilling is needed. */}
        <Route path="/en" element={<Navigate to="/en/" replace />} />
        <Route path="/en/" element={<HomePage />} />
        <Route path="/en/blog" element={<BlogIndexPage />} />
        <Route path="/en/blog/:slug" element={<BlogPostPage />} />
        <Route path="/en/support" element={<SupportPage />} />
        <Route path="/en/work/:slug" element={<CasePage />} />

        {/* Auth/portal/admin: no EN mirror — these are functional flows. */}
        <Route path="/checkout/return" element={<CheckoutReturn />} />
        <Route path="/manage" element={<PortalRequestPage />} />
        <Route path="/portal" element={<Navigate to="/manage" replace />} />
        <Route path="/portal/:token" element={<PortalPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <AuthProvider>
          <div className="App">
            <AppRouter />
            <Toaster position="top-right" richColors />
          </div>
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
